import React, { useMemo } from 'react';
import { Pie } from '@nivo/pie';
import { useReportConfiguration } from 'components/reporting/sidebar/ConfiguredReport';
import {
  getColor,
  round,
  generateDefaultConfiguration,
  contrastingTextColor,
  getDisplayValuesForSeries,
  getDisplayValueForDatum,
  openDrawerForDrillIn,
  onIdeaPortalSettingsPage,
} from '../helpers';
import { createSafeStylesAccessor } from '../helpers/cssHelpers';
import { BasicTooltip } from '@nivo/tooltip';
import { useChartScales } from './ChartScales';
import styles from './Chart.module.css';
import classNames from 'classnames';

/**
 * @typedef PieChartProps
 * @prop {*=} data
 * @prop {*=} meta
 * @prop {*=} width
 * @prop {*=} height
 * @prop {*=} publishing
 */
/**
 * @type {React.FC<PieChartProps>}
 */
function PieChart({ data, meta, width, height, publishing }) {
  const { get } = useReportConfiguration();
  const safeStyles = createSafeStylesAccessor(get() || {});
  const dataLabelFormat = safeStyles.getDataLabelFormat();

  const chartProps = generateDefaultConfiguration({
    configuration: get(),
    meta,
    width,
  });

  const { margins } = useChartScales();

  const sliceValues = useMemo(() => {
    const percentageMap = {};
    const dataTotal = data.reduce((sum, slice) => sum + slice.value, 0);

    data.forEach(slice => {
      percentageMap[slice[meta.columnKey]] = {
        percent: round((slice.value / dataTotal) * 100, 2),
        actual: slice.value,
      };
    });

    return percentageMap;
  }, [data, meta.columnKey]);

  const sliceDisplayType = get('sliceData');
  const chartData = useMemo(() => {
    let pieData;
    if (sliceDisplayType === 'percentage') {
      pieData = data.map(slice => ({
        ...slice,
        value: sliceValues[slice[meta.columnKey]].percent,
      }));
    } else {
      pieData = data;
    }

    return pieData;
  }, [data, sliceDisplayType, meta.columnKey, sliceValues]);

  chartProps.theme.labels = {
    text: dataLabelFormat,
  };

  const drillInEnabled = meta.drill_in?.enabled && !onIdeaPortalSettingsPage();
  function handleClick(node, event) {
    if (drillInEnabled) {
      openDrawerForDrillIn(node.data.column_index, null, meta, event.target);
    }
  }

  const classes = classNames(styles.PieChartContainer, {
    [styles.clickable]: drillInEnabled,
  });

  return (
    <div className={classes}>
      <Pie
        {...chartProps}
        width={width}
        height={height}
        margin={margins}
        data={chartData}
        id={meta.columnKey}
        innerRadius={get('layout') === 'donut' ? get('radius') / 100 : 0}
        arcLabelsLinkStrokeWidth={2}
        arcLabelsLinkColor={'var(--aha-gray-500)'}
        arcLabelsTextColor={arc =>
          dataLabelFormat.color === arc.color
            ? contrastingTextColor(arc.color)
            : dataLabelFormat.color
        }
        arcLabel={datum => {
          if (!get('showLabels')) return null;
          const { id, value } = datum;
          const seriesDisplayLabel = getDisplayValuesForSeries(meta, id);
          if (get('seriesLabels') === 'inside') return seriesDisplayLabel;
          return sliceDisplayType === 'percentage'
            ? `${value}%`
            : getDisplayValueForDatum(datum);
        }}
        colors={slice => getColor(get(), meta, slice.id)}
        enableArcLinkLabels={get('seriesLabels') === 'outside'}
        arcLinkLabel={({ id }) => {
          const seriesDisplayLabel = getDisplayValuesForSeries(meta, id);
          return seriesDisplayLabel;
        }}
        tooltip={({ datum: slice }) => {
          const { percent } = sliceValues[slice.data[meta.columnKey]];
          const { color, id } = slice;
          const seriesDisplayLabel = getDisplayValuesForSeries(meta, id);
          const displayValue = getDisplayValueForDatum(slice);

          return (
            <BasicTooltip
              id={seriesDisplayLabel}
              value={`${displayValue} (${percent}%)`}
              enableChip
              color={color}
            />
          );
        }}
        layers={['arcs', 'arcLinkLabels', 'arcLabels']}
        startAngle={90}
        endAngle={-270}
        arcLabelsSkipAngle={24}
        arcLinkLabelsSkipAngle={5}
        animate={false}
        borderColor={get('border_color')}
        borderWidth={get('border_color') !== 'none' ? 1 : 0}
        onClick={handleClick}
      />
    </div>
  );
}

export default PieChart;
